import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  featuredInstitutions,
  homepageFindings,
  impactSignals,
  researchDashboards,
  researchOutputs,
} from "../data/research";
import { DATA_REPO_LINK } from "../constants";

const Home = () => {
  const theme = useTheme();
  const featuredResearchOutputs = researchOutputs.slice(0, 2);

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "auto",
        padding: { lg: "78px 0 180px", sm: "68px 0 140px", xs: "42px 0 96px" },
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          minHeight: { lg: "610px", md: "540px", xs: "auto" },
          padding: { lg: "42px 0 76px", sm: "48px 0 68px", xs: "54px 0 62px" },
        }}
      >
        <Stack spacing={{ lg: 4, xs: 3 }} sx={{ maxWidth: "1040px" }}>
          <Typography
            sx={{
              color: theme.customColors.supplementary.bluish,
              fontSize: { lg: "15px", xs: "12px" },
              fontWeight: 800,
              letterSpacing: "0.1em",
              lineHeight: 1,
              textTransform: "uppercase",
            }}
          >
            Data Provenance Initiative
          </Typography>
          <Typography
            sx={{
              fontSize: { lg: "88px", md: "68px", sm: "52px", xs: "39px" },
              lineHeight: "0.95",
              fontWeight: 700,
              color: theme.customColors.primary.aliceBlue,
              maxWidth: "1100px",
            }}
            variant="h1"
          >
            Public evidence for accountable AI
          </Typography>
          <Typography
            sx={{
              fontSize: { lg: "25px", md: "22px", xs: "18px" },
              lineHeight: 1.45,
              fontWeight: 450,
              color: theme.customColors.primary.aliceBlue,
              maxWidth: "800px",
            }}
          >
            DPI maps the datasets, web policies, model markets, and real-world
            AI uses that shape how AI systems are built and governed.
          </Typography>
          <Box>
            <Link to="/publications">
              <Button
                variant="contained"
                endIcon={<ArrowOutwardIcon />}
                sx={{
                  backgroundColor: theme.customColors.primary.aliceBlue,
                  borderRadius: "8px",
                  color: theme.customColors.primary.prusianBlue,
                  fontSize: "16px",
                  fontWeight: 800,
                  padding: "11px 18px",
                  ":hover": {
                    backgroundColor: theme.customColors.primary.lion,
                    color: "#fff",
                  },
                }}
              >
                Explore research
              </Button>
            </Link>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          borderTop: `1px solid rgba(222, 243, 255, 0.28)`,
          borderBottom: `1px solid rgba(222, 243, 255, 0.28)`,
          padding: { lg: "26px 0", xs: "24px 0" },
        }}
      >
        <Stack spacing={{ lg: 3, xs: 2.5 }}>
          <Box
            sx={{
              alignItems: { lg: "center", xs: "flex-start" },
              display: "flex",
              flexDirection: { lg: "row", xs: "column" },
              gap: { lg: "28px", xs: "16px" },
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                color: theme.customColors.primary.aliceBlue,
                fontSize: { lg: "20px", xs: "17px" },
                fontWeight: 650,
                lineHeight: 1.35,
                maxWidth: "620px",
              }}
            >
              Covered, cited, and used across research, journalism, policy, and
              public-interest AI governance.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {featuredInstitutions.map((institution) => (
                <Typography
                  key={institution}
                  sx={{
                    border: "1px solid rgba(222, 243, 255, 0.35)",
                    borderRadius: "999px",
                    color: theme.customColors.primary.aliceBlue,
                    fontSize: "12px",
                    fontWeight: 800,
                    lineHeight: 1,
                    padding: "8px 10px",
                  }}
                >
                  {institution}
                </Typography>
              ))}
            </Box>
          </Box>
          <Grid container spacing={{ lg: 0, xs: 2 }}>
            {impactSignals.map((signal, index) => (
              <Grid item lg={3} sm={6} xs={12} key={signal.label}>
                <Box
                  sx={{
                    borderLeft: {
                      lg:
                        index === 0 ? 0 : "1px solid rgba(222, 243, 255, 0.22)",
                      xs: "0",
                    },
                    padding: {
                      lg: index === 0 ? "0 24px 0 0" : "0 24px",
                      xs: 0,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: theme.customColors.primary.aliceBlue,
                      fontSize: { lg: "30px", xs: "26px" },
                      fontWeight: 850,
                      lineHeight: 1,
                      marginBottom: "5px",
                    }}
                  >
                    {signal.value}
                  </Typography>
                  <Typography
                    sx={{
                      color: theme.customColors.supplementary.bluish,
                      fontSize: "14px",
                      fontWeight: 800,
                      lineHeight: 1.25,
                    }}
                  >
                    {signal.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Box>

      <Box
        sx={{
          marginTop: { lg: "84px", xs: "56px" },
          marginBottom: { lg: "76px", xs: "52px" },
        }}
      >
        <Box
          sx={{
            alignItems: { lg: "end", xs: "start" },
            display: "flex",
            flexDirection: { lg: "row", xs: "column" },
            justifyContent: "space-between",
            gap: "22px",
            marginBottom: { lg: "28px", xs: "22px" },
          }}
        >
          <Box>
            <Typography
              sx={{
                color: theme.customColors.supplementary.bluish,
                fontSize: "13px",
                fontWeight: 800,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: "10px",
              }}
            >
              Flagship systems
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: theme.customColors.primary.aliceBlue,
                fontSize: { lg: "48px", xs: "34px" },
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              Four public measurement systems
            </Typography>
            <Typography
              sx={{
                color: theme.customColors.supplementary.bluish,
                fontSize: { lg: "18px", xs: "16px" },
                lineHeight: 1.45,
                marginTop: "16px",
                maxWidth: "680px",
              }}
            >
              Together they trace AI from source data and web consent to model
              markets and real-world use.
            </Typography>
          </Box>
          <Link to={DATA_REPO_LINK} target="_blank" rel="noreferrer">
            <Button
              variant="outlined"
              endIcon={<ArrowOutwardIcon />}
              sx={{
                borderColor: "rgba(222, 243, 255, 0.56)",
                borderRadius: "8px",
                color: theme.customColors.primary.aliceBlue,
                fontSize: "16px",
                fontWeight: 800,
                padding: "10px 18px",
                ":hover": {
                  borderColor: theme.customColors.primary.lion,
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                },
              }}
            >
              Use the data
            </Button>
          </Link>
        </Box>
        <Grid container spacing={2}>
          {researchDashboards.map((dashboard) => (
            <Grid item lg={3} md={6} xs={12} key={dashboard.title}>
              <Box
                component={dashboard.comingSoon ? "div" : "a"}
                href={dashboard.comingSoon ? undefined : dashboard.href}
                target={
                  dashboard.external && !dashboard.comingSoon
                    ? "_blank"
                    : undefined
                }
                rel={
                  dashboard.external && !dashboard.comingSoon
                    ? "noreferrer"
                    : undefined
                }
                sx={{
                  backgroundColor: "rgba(255, 251, 248, 0.98)",
                  border: "1px solid rgba(222, 243, 255, 0.24)",
                  borderRadius: "8px",
                  color: theme.customColors.supplementary.darkBlue,
                  cursor: dashboard.comingSoon ? "default" : "pointer",
                  display: "block",
                  height: "100%",
                  minHeight: { lg: "315px", xs: "auto" },
                  overflow: "hidden",
                  padding: { lg: "28px", xs: "24px" },
                  position: "relative",
                  textDecoration: "none",
                  transition: "background-color 180ms ease, color 180ms ease",
                  "&::before": {
                    backgroundColor: dashboard.accent,
                    content: '""',
                    height: "4px",
                    left: 0,
                    position: "absolute",
                    right: 0,
                    top: 0,
                  },
                  ":hover": {
                    ...(dashboard.comingSoon
                      ? {}
                      : {
                          backgroundColor: theme.customColors.primary.lion,
                          color: theme.customColors.supplementary.light,
                          ".system-label, .system-metric, .system-cta": {
                            color: theme.customColors.primary.aliceBlue,
                          },
                        }),
                  },
                }}
              >
                <Stack sx={{ height: "100%" }} spacing={2}>
                  <Typography
                    className="system-label"
                    sx={{
                      color: theme.customColors.supplementary.horizon,
                      fontSize: "12px",
                      fontWeight: 800,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                    }}
                  >
                    {dashboard.label}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { lg: "27px", xs: "24px" },
                      lineHeight: 1.06,
                      fontWeight: 800,
                    }}
                  >
                    {dashboard.title}
                  </Typography>
                  <Typography
                    className="system-metric"
                    sx={{
                      color: theme.customColors.primary.lion,
                      fontSize: "16px",
                      fontWeight: 850,
                      lineHeight: 1.2,
                    }}
                  >
                    {dashboard.metric}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { lg: "16px", xs: "15px" },
                      lineHeight: 1.45,
                      fontWeight: 450,
                    }}
                  >
                    {dashboard.description}
                  </Typography>
                  <Typography
                    className="system-cta"
                    sx={{
                      alignItems: "center",
                      color: theme.customColors.primary.lion,
                      display: "flex",
                      fontSize: "16px",
                      fontWeight: 800,
                      gap: "7px",
                      marginTop: "auto",
                    }}
                  >
                    {dashboard.cta}
                    {dashboard.comingSoon ? null : (
                      <ArrowOutwardIcon sx={{ fontSize: "18px" }} />
                    )}
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Grid
        container
        spacing={3}
        sx={{ marginBottom: { lg: "76px", xs: "52px" } }}
      >
        {featuredResearchOutputs.map((item) => (
          <Grid item md={6} xs={12} key={item.title}>
            <Box
              component={item.comingSoon ? "div" : Link}
              to={item.comingSoon ? undefined : item.href}
              target={item.external && !item.comingSoon ? "_blank" : undefined}
              rel={item.external && !item.comingSoon ? "noreferrer" : undefined}
              sx={{
                color: "inherit",
                cursor: item.comingSoon ? "default" : "pointer",
                display: "block",
                height: "100%",
                textDecoration: "none",
              }}
            >
              <Card
                sx={{
                  backgroundColor: item.comingSoon
                    ? "rgba(222, 243, 255, 0.95)"
                    : "rgba(255, 251, 248, 0.98)",
                  borderRadius: "8px",
                  color: theme.customColors.primary.prusianBlue,
                  height: "100%",
                }}
              >
                <CardContent sx={{ padding: { lg: "34px", xs: "24px" } }}>
                  <Typography
                    sx={{
                      color: theme.customColors.primary.lion,
                      fontSize: "12px",
                      fontWeight: 800,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      marginBottom: "16px",
                    }}
                  >
                    {item.tag}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { lg: "34px", xs: "26px" },
                      fontWeight: 800,
                      lineHeight: 1.08,
                      marginBottom: "16px",
                    }}
                    variant="h3"
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { lg: "18px", xs: "16px" },
                      lineHeight: 1.48,
                      maxWidth: "720px",
                    }}
                  >
                    {item.description}
                  </Typography>
                  <Typography
                    sx={{
                      alignItems: "center",
                      color: theme.customColors.primary.lion,
                      display: "flex",
                      fontSize: "16px",
                      fontWeight: 800,
                      gap: "7px",
                      marginTop: "28px",
                    }}
                  >
                    {item.comingSoon
                      ? "Coming soon"
                      : item.external
                        ? "Open platform"
                        : "Read paper"}
                    {item.comingSoon ? null : (
                      <ArrowOutwardIcon sx={{ fontSize: "18px" }} />
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          borderTop: `1px solid rgba(222, 243, 255, 0.28)`,
          borderBottom: `1px solid rgba(222, 243, 255, 0.28)`,
          padding: { lg: "28px 0", xs: "22px 0" },
        }}
      >
        <Grid container spacing={3}>
          {homepageFindings.map((finding) => (
            <Grid item md={4} xs={12} key={finding.label}>
              <Typography
                sx={{
                  color: theme.customColors.supplementary.bluish,
                  fontSize: "12px",
                  fontWeight: 800,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  marginBottom: "10px",
                }}
              >
                {finding.label}
              </Typography>
              <Typography
                sx={{
                  color: theme.customColors.primary.aliceBlue,
                  fontSize: { lg: "21px", xs: "18px" },
                  fontWeight: 650,
                  lineHeight: 1.28,
                  marginBottom: "10px",
                }}
              >
                {finding.value}
              </Typography>
              <Typography
                sx={{
                  color: theme.customColors.supplementary.bluish,
                  fontSize: "14px",
                  lineHeight: 1.35,
                }}
              >
                {finding.source}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default Home;
